import { useState } from 'react';
import { Plus, Trash, Edit, Check, X, Link } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ImageUploader } from '@/components/ui/image-uploader';
import { LazyImage } from '@/components/ui/lazy-image';

interface ProductRecommendation {
  id: string;
  name: string;
  description?: string;
  image_url?: string;
  price?: number;
  currency?: string;
  affiliate_link: string;
  category?: string;
}

interface ProductRecommendationEditorProps {
  products: ProductRecommendation[];
  onAddProduct: (product: ProductRecommendation) => void;
  onRemoveProduct: (id: string) => void;
  onUpdateProduct: (product: ProductRecommendation) => void;
}

export const ProductRecommendationEditor = ({
  products,
  onAddProduct,
  onRemoveProduct,
  onUpdateProduct
}: ProductRecommendationEditorProps) => {
  const [newProduct, setNewProduct] = useState<ProductRecommendation>({
    id: uuidv4(),
    name: '',
    description: '',
    image_url: '',
    price: undefined,
    currency: 'USD',
    affiliate_link: '',
    category: 'Gear'
  });
  
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [editingProduct, setEditingProduct] = useState<ProductRecommendation | null>(null);

  const handleNewProductChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewProduct(prev => ({
      ...prev,
      [name]: name === 'price' ? (value ? parseFloat(value) : undefined) : value
    }));
  };

  const handleEditProductChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!editingProduct) return;
    
    const { name, value } = e.target;
    setEditingProduct(prev => ({
      ...prev!,
      [name]: name === 'price' ? (value ? parseFloat(value) : undefined) : value
    }));
  };

  const handleAddProduct = () => {
    if (!newProduct.name) {
      toast.error('Product name is required');
      return;
    }
    
    if (!newProduct.affiliate_link) {
      toast.error('Affiliate link is required');
      return;
    }
    
    onAddProduct({
      ...newProduct,
      id: uuidv4() // Generate new ID to ensure uniqueness
    });
    
    // Reset form
    setNewProduct({
      id: uuidv4(),
      name: '',
      description: '',
      image_url: '',
      price: undefined,
      currency: 'USD',
      affiliate_link: '',
      category: 'Gear'
    });
  };

  const startEditing = (product: ProductRecommendation) => {
    setEditingProductId(product.id);
    setEditingProduct({ ...product });
  };

  const cancelEditing = () => {
    setEditingProductId(null);
    setEditingProduct(null);
  };

  const saveEditing = () => {
    if (!editingProduct) return;
    
    if (!editingProduct.name) {
      toast.error('Product name is required');
      return;
    }
    
    if (!editingProduct.affiliate_link) {
      toast.error('Affiliate link is required');
      return;
    }
    
    onUpdateProduct(editingProduct);
    cancelEditing();
  };

  const handleImageUploaded = (imageUrl: string) => {
    setNewProduct(prev => ({ ...prev, image_url: imageUrl }));
  };

  const handleEditingImageUploaded = (imageUrl: string) => {
    if (!editingProduct) return;
    setEditingProduct(prev => ({ ...prev!, image_url: imageUrl }));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Product Recommendations</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Add New Product</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="product-name">Product Name*</Label>
              <Input
                id="product-name"
                name="name"
                value={newProduct.name}
                onChange={handleNewProductChange}
                placeholder="e.g., Travel Backpack"
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="product-category">Category</Label>
              <Input
                id="product-category"
                name="category"
                value={newProduct.category}
                onChange={handleNewProductChange}
                placeholder="e.g., Gear, Clothing, Electronics"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="product-price">Price</Label>
              <div className="flex">
                <div className="w-16 flex items-center justify-center border border-r-0 rounded-l-md bg-muted">
                  <span className="text-sm">$</span>
                </div>
                <Input
                  id="product-price"
                  name="price"
                  type="number"
                  value={newProduct.price || ''}
                  onChange={handleNewProductChange}
                  placeholder="29.99"
                  className="rounded-l-none"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="affiliate-link">Affiliate Link*</Label>
              <div className="flex rounded-md">
                <div className="relative flex items-center">
                  <Link className="absolute left-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="affiliate-link"
                    name="affiliate_link"
                    value={newProduct.affiliate_link}
                    onChange={handleNewProductChange}
                    placeholder="https://example.com/product?ref=your-affiliate-id"
                    className="pl-9"
                    required
                  />
                </div>
              </div>
            </div>
            
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="product-description">Description</Label>
              <Textarea
                id="product-description"
                name="description"
                value={newProduct.description}
                onChange={handleNewProductChange}
                placeholder="Brief description of the product and why you recommend it"
                rows={3}
              />
            </div>
            
            <div className="space-y-2 md:col-span-2">
              <ImageUploader
                onImageUploaded={handleImageUploaded}
                currentImage={newProduct.image_url || ''}
                label="Upload product image"
                aspectRatio={1}
              />
            </div>
          </div>
          
          <Button type="button" onClick={handleAddProduct} className="mt-2">
            <Plus className="h-4 w-4 mr-2" /> Add Product
          </Button>
        </div>
        
        <Separator />
        
        <div className="space-y-4">
          <h3 className="text-lg font-medium">Product List</h3>
          
          {products.length === 0 ? (
            <div className="text-center text-muted-foreground py-4">
              No products added yet. Add product recommendations with affiliate links.
            </div>
          ) : (
            <div className="space-y-4">
              {products.map(product => (
                <div key={product.id} className="border rounded-md p-4">
                  {editingProductId === product.id ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor={`edit-name-${product.id}`}>Product Name*</Label>
                          <Input
                            id={`edit-name-${product.id}`}
                            name="name"
                            value={editingProduct?.name || ''}
                            onChange={handleEditProductChange}
                            required
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor={`edit-category-${product.id}`}>Category</Label>
                          <Input
                            id={`edit-category-${product.id}`}
                            name="category"
                            value={editingProduct?.category || ''}
                            onChange={handleEditProductChange}
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor={`edit-price-${product.id}`}>Price</Label>
                          <div className="flex">
                            <div className="w-16 flex items-center justify-center border border-r-0 rounded-l-md bg-muted">
                              <span className="text-sm">$</span>
                            </div>
                            <Input
                              id={`edit-price-${product.id}`}
                              name="price"
                              type="number"
                              value={editingProduct?.price || ''}
                              onChange={handleEditProductChange}
                              className="rounded-l-none"
                            />
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor={`edit-link-${product.id}`}>Affiliate Link*</Label>
                          <div className="flex rounded-md">
                            <div className="relative flex items-center">
                              <Link className="absolute left-2.5 h-4 w-4 text-muted-foreground" />
                              <Input
                                id={`edit-link-${product.id}`}
                                name="affiliate_link"
                                value={editingProduct?.affiliate_link || ''}
                                onChange={handleEditProductChange}
                                className="pl-9"
                                required
                              />
                            </div>
                          </div>
                        </div>
                        
                        <div className="space-y-2 md:col-span-2">
                          <Label htmlFor={`edit-description-${product.id}`}>Description</Label>
                          <Textarea
                            id={`edit-description-${product.id}`}
                            name="description"
                            value={editingProduct?.description || ''}
                            onChange={handleEditProductChange}
                            rows={3}
                          />
                        </div>
                        
                        <div className="space-y-2 md:col-span-2">
                          <ImageUploader
                            onImageUploaded={handleEditingImageUploaded}
                            currentImage={editingProduct?.image_url || ''}
                            label="Upload product image"
                            aspectRatio={1}
                          />
                        </div>
                      </div>
                      
                      <div className="flex justify-end space-x-2 mt-4">
                        <Button variant="outline" size="sm" onClick={cancelEditing}>
                          <X className="h-4 w-4 mr-1" /> Cancel
                        </Button>
                        <Button size="sm" onClick={saveEditing}>
                          <Check className="h-4 w-4 mr-1" /> Save
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-4">
                      {product.image_url && (
                        <div className="flex-shrink-0 w-24 h-24 md:w-32 md:h-32 rounded-md overflow-hidden">
                          <LazyImage 
                            src={product.image_url} 
                            alt={product.name} 
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-lg truncate">{product.name}</h4>
                          <div className="flex space-x-1">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => startEditing(product)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => onRemoveProduct(product.id)}
                            >
                              <Trash className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        
                        {product.category && (
                          <p className="text-sm text-muted-foreground">
                            Category: {product.category}
                          </p>
                        )}
                        
                        {product.price && (
                          <p className="text-sm font-medium mt-1">
                            ${product.price.toFixed(2)} {product.currency}
                          </p>
                        )}
                        
                        {product.description && (
                          <p className="text-sm mt-2 line-clamp-2">{product.description}</p>
                        )}
                        
                        <div className="mt-2 flex items-center text-sm text-muted-foreground">
                          <Link className="h-3 w-3 mr-1" />
                          <span className="truncate">{product.affiliate_link}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
